/**
 * SEO image-dimension normalizer.
 *
 * Adds intrinsic width and height attributes only when an image is missing
 * either value. It supports regular, lazy-loaded, app-injected, and
 * template-contained images without changing image sources, alt text,
 * styling, product data, or swatch/filter values.
 */
(() => {
  const observedImages = new WeakSet();
  const probedSources = new WeakMap();
  const probeQueue = [];
  let activeProbes = 0;
  const maxConcurrentProbes = 4;
  let pendingRootScan = false;

  const hasImageDimensions = (image) =>
    image.hasAttribute('width') && image.hasAttribute('height');

  const setDimensions = (image, width, height) => {
    if (!width || !height) return;

    if (!image.hasAttribute('width')) image.setAttribute('width', String(width));
    if (!image.hasAttribute('height')) image.setAttribute('height', String(height));
  };

  const getImageSource = (image) =>
    image.dataset.src || image.currentSrc || image.getAttribute('src');

  const runProbeQueue = () => {
    while (activeProbes < maxConcurrentProbes && probeQueue.length) {
      const { image, source } = probeQueue.shift();
      activeProbes += 1;

      const probe = new Image();
      const finish = () => {
        activeProbes -= 1;
        runProbeQueue();
      };

      probe.addEventListener(
        'load',
        () => {
          setDimensions(image, probe.naturalWidth, probe.naturalHeight);
          finish();
        },
        { once: true }
      );
      probe.addEventListener('error', finish, { once: true });
      probe.src = source;
    }
  };

  const queueProbe = (image) => {
    const source = getImageSource(image);
    if (!source || probedSources.get(image) === source) return;

    probedSources.set(image, source);
    probeQueue.push({ image, source });
    runProbeQueue();
  };

  const normalizeImage = (image) => {
    if (!(image instanceof HTMLImageElement) || hasImageDimensions(image)) return;

    if (image.naturalWidth && image.naturalHeight) {
      setDimensions(image, image.naturalWidth, image.naturalHeight);
      return;
    }

    if (observedImages.has(image)) return;
    observedImages.add(image);

    image.addEventListener(
      'load',
      () => setDimensions(image, image.naturalWidth, image.naturalHeight),
      { once: true }
    );

    queueProbe(image);
  };

  const resolveTemplateImage = (image) => {
    if (!(image instanceof HTMLImageElement) || hasImageDimensions(image)) return;
    queueProbe(image);
  };

  const scanRoot = (root) => {
    if (!root) return;

    if (root instanceof HTMLImageElement) normalizeImage(root);
    root.querySelectorAll?.('img').forEach(normalizeImage);

    const templates = [];
    if (root instanceof HTMLTemplateElement) templates.push(root);
    root.querySelectorAll?.('template').forEach((template) => templates.push(template));

    templates.forEach((template) => {
      template.content.querySelectorAll('img').forEach(resolveTemplateImage);
    });
  };

  const queueDocumentScan = () => {
    if (pendingRootScan) return;
    pendingRootScan = true;

    requestAnimationFrame(() => {
      pendingRootScan = false;
      scanRoot(document);
    });
  };

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes') {
        normalizeImage(record.target);
        return;
      }

      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
          scanRoot(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'data-src', 'data-srcset']
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueDocumentScan, { once: true });
  } else {
    queueDocumentScan();
  }
})();
